// Extracted from original/src/App.tsx:48754-49548 (`showCustomerTransfer`, ADR-08 §2.1): frame, currency switch,
// the sender / recipient columns (type filter, search, selected card with "تغيير الحساب", result list), amount, note,
// "تسجيل مع طباعة" / "تسجيل العملية". Kept: every class string, icon, the "د.أ" unit this dialog used for USD and the
// list filters (name or account number, type) — presentation only.
// Cut: `debts` (stored balances) → `options` with balances from the ledger (D-013); `formatNumber` → preformatted
// labels; `handleCustomerTransfer` with its on-screen 4-digit code and the operations security code
// (`activeConfirmationTask`, :49415-49530) → `onSubmit` (preview → personal PIN → commit, D-073 (5), D-018);
// `shouldPrintTransfer` → the `print` argument; the `1000 + id` account-number display → `accountNumber`.
// Changed by decision: when a cross-type transfer would take the recipient below zero the surplus options of the
// adjustment dialog appear above the buttons (D-073 (2) — V-25); the USD button follows the office setting (D-066).
import { useRef, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { ArrowLeftRight, RefreshCw, X } from 'lucide-react';
import { NumericInput } from '../components/NumericInput.js';
import { useI18n } from '../i18n-context.js';
import { SurplusOptions, type CustomerCurrency, type CustomerKindKey, type SurplusOptionsProps } from './CustomerModals.js';

export interface TransferPick {
  readonly id: string;
  readonly name: string;
  readonly accountNumber: string;
  readonly kind: CustomerKindKey;
  /** Balance in the chosen currency, preformatted without the unit. */
  readonly balanceLabel: string;
  readonly negative: boolean;
}

export type TransferTypeFilter = 'all' | CustomerKindKey;

export interface TransferSide {
  readonly id: string;
  readonly typeFilter: TransferTypeFilter;
  readonly search: string;
}

export interface CustomerTransferModalProps {
  readonly currency: CustomerCurrency;
  readonly onCurrencyChange: (c: CustomerCurrency) => void;
  readonly usdHidden: boolean;
  readonly options: readonly TransferPick[];
  readonly from: TransferSide;
  readonly to: TransferSide;
  readonly onFromChange: (patch: Partial<TransferSide>) => void;
  readonly onToChange: (patch: Partial<TransferSide>) => void;
  readonly amount: string;
  readonly onAmountChange: (v: string) => void;
  readonly note: string;
  readonly onNoteChange: (v: string) => void;
  /** D-073 (2): non-null when the recipient would go below zero (cross-type). */
  readonly surplus: { readonly type: 'debt_overpaid' | 'trust_overdrawn'; readonly recipientName: string; readonly message: string } | null;
  readonly surplusProps: Omit<SurplusOptionsProps, 'type' | 'customerName'>;
  readonly busy?: boolean;
  readonly onSubmit: (print: boolean) => void;
  readonly onClose: () => void;
}

export function CustomerTransferModal(props: CustomerTransferModalProps) {
  const { t } = useI18n();
  const currencySymbol = props.currency === "USD" ? "د.أ" : "د.ع";
  const printChoice = useRef(false);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!props.busy) props.onSubmit(printChoice.current);
  };

  const column = (side: 'from' | 'to') => {
    const s = side === 'from' ? props.from : props.to;
    const set = side === 'from' ? props.onFromChange : props.onToChange;
    const selected = s.id ? props.options.find((d) => d.id === s.id) : undefined;
    const selectedTone = side === 'from'
      ? { card: "p-3.5 bg-blue-50/90 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-xl space-y-2", badge: "text-[10px] bg-blue-600 text-white font-bold px-2 py-0.5 rounded-full shrink-0", line: "pt-2 border-t border-blue-200/60 dark:border-blue-800/40 flex items-center justify-between font-mono" }
      : { card: "p-3.5 bg-emerald-50/90 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-xl space-y-2", badge: "text-[10px] bg-emerald-600 text-white font-bold px-2 py-0.5 rounded-full shrink-0", line: "pt-2 border-t border-emerald-200/60 dark:border-emerald-800/40 flex items-center justify-between font-mono" };
    const q = s.search.trim().toLowerCase();
    const filtered = props.options.filter((d) => {
      if (s.typeFilter !== "all" && d.kind !== s.typeFilter) return false;
      if (!q) return true;
      return d.name.toLowerCase().includes(q) || d.accountNumber.toLowerCase().includes(q);
    });
    const typeWord = (k: CustomerKindKey) => (k === "trust" ? t('cust.trust_one') : t('cust.debt_one'));
    return (
      <div className="bg-slate-50/50 dark:bg-slate-900/10 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/80 space-y-3 flex flex-col justify-start" data-testid={`transfer-${side}`}>
        <div className="flex items-center justify-between gap-2">
          <label className={side === 'from' ? "block text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest" : "block text-xs font-bold text-indigo-600 dark:text-indigo-400 tracking-widest uppercase"}>
            {side === 'from' ? t('fromCustomer') : t('toCustomer')}
          </label>
          <div className="flex items-center bg-slate-200/80 dark:bg-slate-800/80 p-0.5 rounded-lg text-[11px] font-bold">
            <button
              type="button"
              onClick={() => set({ typeFilter: "all", id: "" })}
              className={`px-2 py-0.5 rounded-md transition-all ${
                s.typeFilter === "all"
                  ? "bg-white dark:bg-[#161b22] text-gray-900 dark:text-white shadow-sm font-black"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {t('cust.filter_all')}
            </button>
            <button
              type="button"
              data-testid={`transfer-${side}-debts`}
              onClick={() => set({ typeFilter: "debt", id: "" })}
              className={`px-2 py-0.5 rounded-md transition-all ${
                s.typeFilter === "debt"
                  ? "bg-rose-600 text-white shadow-sm font-black"
                  : "text-gray-600 dark:text-gray-400 hover:text-rose-600 dark:hover:text-rose-400"
              }`}
            >
              {t('cust.debts_plural')}
            </button>
            <button
              type="button"
              data-testid={`transfer-${side}-trusts`}
              onClick={() => set({ typeFilter: "trust", id: "" })}
              className={`px-2 py-0.5 rounded-md transition-all ${
                s.typeFilter === "trust"
                  ? "bg-emerald-600 text-white shadow-sm font-black"
                  : "text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
              }`}
            >
              {t('cust.trusts_plural')}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <input
            type="text"
            data-testid={`transfer-${side}-search`}
            placeholder={side === 'from' ? t('cust.transfer_from_search') : t('cust.transfer_to_search')}
            value={s.search}
            onChange={(e) => set({ search: e.target.value, id: "" })}
            className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-bold"
          />
          <div className="max-h-48 overflow-y-auto border border-gray-150 dark:border-gray-800 rounded-xl bg-white dark:bg-[#161b22] divide-y divide-slate-100 dark:divide-slate-800/60 shadow-inner">
            {selected ? (
              <div className={selectedTone.card}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-right space-y-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-black text-slate-900 dark:text-white text-xs sm:text-sm">
                        {selected.name}
                      </span>
                      <span className={selectedTone.badge}>
                        {side === 'from' ? t('cust.transfer_sender') : t('cust.transfer_recipient')}
                      </span>
                    </div>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 font-bold">
                      {t('cust.account_word')}:
                      #{selected.accountNumber} |{" "}
                      {typeWord(selected.kind)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => set({ id: "" })}
                    className="text-[10px] font-black text-rose-600 hover:text-rose-700 dark:text-rose-400 bg-white dark:bg-[#161b22] hover:bg-rose-50 dark:hover:bg-rose-950/50 px-2.5 py-1 rounded-lg transition-all border border-rose-200 dark:border-rose-800/60 shadow-sm flex items-center gap-1 shrink-0"
                    title={t('cust.change_selected')}
                  >
                    <RefreshCw size={11} />
                    <span>{t('cust.change_account')}</span>
                  </button>
                </div>
                <div className={selectedTone.line}>
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                    {selected.kind === "trust" ? t('cust.trust_amount') : t('cust.amount_due')}
                  </span>
                  <span className={`text-xs sm:text-sm font-black ${selected.negative ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                    {selected.balanceLabel} {currencySymbol}
                  </span>
                </div>
              </div>
            ) : !q && s.typeFilter === "all" ? (
              <div className="p-4 text-center text-xs text-amber-500 font-bold flex flex-col items-center justify-center gap-1">
                <span>{t('cust.transfer_hint')}</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400 font-bold">
                {t('cust.transfer_no_match')}
              </div>
            ) : (
              filtered.map((d) => {
                const isSelected = s.id === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`transfer-${side}-pick-${d.id}`}
                    onClick={() => set({ id: d.id })}
                    className={`w-full text-right px-3 py-2 sm:py-3 flex items-center justify-between text-xs font-bold transition-all ${
                      isSelected
                        ? "bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 shadow-sm border-r-4 border-blue-500"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                    }`}
                  >
                    <div className="text-right">
                      <span className="block font-black text-slate-900 dark:text-white text-xs sm:text-sm">
                        {d.name}
                      </span>
                      <span className="block text-[10px] text-slate-400 font-bold">
                        {t('cust.account_word')}: #
                        {d.accountNumber} |{" "}
                        {typeWord(d.kind)}
                      </span>
                    </div>
                    <div className="text-left font-mono">
                      <span className={`block font-black text-xs sm:text-sm ${d.negative ? "text-rose-500" : "text-emerald-500"}`}>
                        {d.balanceLabel} {currencySymbol}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden" data-testid="customer-transfer-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-4xl rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border max-h-[calc(100vh-2rem)] md:max-h-[88vh] flex flex-col"
      >
        <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-indigo-50 dark:bg-indigo-900/10 shrink-0">
          <h3 className="text-lg sm:text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] dark:digital-glow uppercase tracking-tight">
            <ArrowLeftRight className="text-indigo-600 dark:text-indigo-400" />
            {t('customerTransfer')}
          </h3>
          <button
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* Currency Section at Top */}
          <div className="bg-gray-50/50 dark:bg-[#0d1117]/40 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
            <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
              {t('currency')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => props.onCurrencyChange("IQD")}
                className={`py-3 rounded-xl border font-bold text-xs sm:text-sm transition-all ${props.currency === "IQD" ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20" : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#30363d]"}`}
              >
                {t('iqd')}
              </button>
              {!props.usdHidden && (
                <button
                  type="button"
                  data-testid="transfer-usd"
                  onClick={() => props.onCurrencyChange("USD")}
                  className={`py-3 rounded-xl border font-bold text-xs sm:text-sm transition-all ${props.currency === "USD" ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20" : "bg-gray-50 dark:bg-[#05070a] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-[#30363d]"}`}
                >
                  {t('usd')}
                </button>
              )}
            </div>
          </div>
          {/* Sender & Recipient Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-2">
            {column('from')}
            {column('to')}
          </div>
          {/* Amount and Note - Horizontal or Stacking depending on viewport */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('transferAmount')}
              </label>
              <NumericInput
                required
                value={props.amount ?? ''}
                onChange={(val) => props.onAmountChange(val)}
                className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-mono transition-all font-black text-base"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('transferNote')}
              </label>
              <input
                value={props.note || ''}
                onChange={(e) => props.onNoteChange(e.target.value)}
                className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all text-xs sm:text-sm font-bold placeholder:font-normal"
                placeholder={t('cust.optional_note')}
              />
            </div>
          </div>
          {props.surplus && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 dark:bg-orange-950/20 text-orange-800 dark:text-orange-400 rounded-2xl border border-orange-100 dark:border-orange-900/30">
                <p className="text-xs font-semibold leading-relaxed" data-testid="transfer-surplus-message">{props.surplus.message}</p>
              </div>
              <SurplusOptions type={props.surplus.type} customerName={props.surplus.recipientName} {...props.surplusProps} />
            </div>
          )}
          {/* Submit and Action Buttons - Re-aligned with 2 options, styled beautifully */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 shrink-0">
            {/* Record WITH Print */}
            <button
              type="submit"
              data-testid="transfer-submit-print"
              disabled={props.busy}
              onClick={() => { printChoice.current = true; }}
              className="py-4 px-6 bg-emerald-600 hover:bg-emerald-750 font-sans text-white rounded-2xl font-black text-sm uppercase tracking-wide shadow-lg shadow-emerald-600/10 hover:shadow-emerald-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer border border-emerald-500/20"
            >
              <span>🖨️</span>
              {t('cust.record_print')}
            </button>
            {/* Record WITHOUT Print */}
            <button
              type="submit"
              data-testid="transfer-submit"
              disabled={props.busy}
              onClick={() => { printChoice.current = false; }}
              className="py-4 px-6 bg-slate-800 dark:bg-slate-755 hover:bg-slate-900 dark:hover:bg-slate-700 font-sans text-white dark:text-slate-100 rounded-2xl font-black text-sm uppercase tracking-wide shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer border border-slate-700/50"
            >
              <span>💾</span>
              {t('cust.record_only')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
